import { useUnmount } from 'react-use';
import { some } from 'lodash';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';

import type { Drug } from '@/api/domains/drugs.types';
import type { DrugStatusId } from '@/shared/constants/nomenclature-status.constants';
import { createStoreWithReset } from '@/shared/lib/zustand';

import type { NomenclatureModalTab } from './modals/NomenclatureModalTabbar';
import type { NomenclatureModalVariant } from './modals/types';

// ----------------------------------------------------------------------
export type NomenclatureStore = {
  // State
  isFilterPanelVisible: boolean;
  activeTab: NomenclatureModalTab;
  preferredTab: NomenclatureModalTab;
  variant: NomenclatureModalVariant | null;

  // ----------------------------------------------------------------------
  // Modals
  isCreateModalVisible: boolean;
  isDuplicateModalVisible: boolean;
  isEditModalVisible: boolean;

  // ----------------------------------------------------------------------
  // Dialogs
  isDeleteDialogVisible: boolean;
  isDuplicateDialogVisible: boolean;
  isConfirmationCreateDialogVisible: boolean;
  isConfirmationEditDialogVisible: boolean;

  searchQuery: string;
  bindingsSearchQuery: string;

  selectedRow: Drug | null;

  /**
   * Product name for its further creation.
   * e.g. used in bindings
   */
  productNameToCreate: string | null;

  selectedStatusId: DrugStatusId | null;

  // Actions
  actions: {
    showFilterPanel: VoidFunction;
    hideFilterPanel: VoidFunction;

    setPreferredTab: (preferredTab: NomenclatureModalTab) => void;
    setActiveTab: (activeTab: NomenclatureModalTab) => void;

    setVariant: (variant: NomenclatureModalVariant) => void;

    // ----------------------------------------------------------------------
    // Modals
    openCreateModal: VoidFunction;
    closeCreateModal: VoidFunction;

    openDuplicateModal: VoidFunction;
    closeDuplicateModal: VoidFunction;

    openEditModal: VoidFunction;
    closeEditModal: VoidFunction;

    // ----------------------------------------------------------------------
    // Dialogs
    openDeleteDialog: VoidFunction;
    closeDeleteDialog: VoidFunction;

    openDuplicateDialog: VoidFunction;
    closeDuplicateDialog: VoidFunction;

    openConfirmationCreateDialog: VoidFunction;
    closeConfirmationCreateDialog: VoidFunction;

    openConfirmationEditDialog: VoidFunction;
    closeConfirmationEditDialog: VoidFunction;

    // ----------------------------------------------------------------------

    setSearchQuery: (query: string) => void;
    clearSearchQuery: VoidFunction;

    setBindingsSearchQuery: (query: string) => void;
    clearBindingsSearchQuery: VoidFunction;

    setSelectedRow: (selectedRow: Drug) => void;
    resetSelectedRow: VoidFunction;

    setProductNameToCreate: (productNameToCreate: string) => void;
    resetProductNameToCreate: VoidFunction;

    setSelectedStatusId: (id: DrugStatusId) => void;
    resetSelectedStatusId: () => void;
  };
};

// ----------------------------------------------------------------------

export const [useNomenclatureStore, resetNomenclatureStore] = createStoreWithReset<NomenclatureStore>()(
  devtools(
    subscribeWithSelector(set => ({
      // State
      isFilterPanelVisible: false,
      activeTab: 'general',
      preferredTab: 'general',
      variant: null,

      // ----------------------------------------------------------------------
      // Modals
      isCreateModalVisible: false,
      isDuplicateModalVisible: false,
      isEditModalVisible: false,

      // ----------------------------------------------------------------------
      // Dialogs
      isDeleteDialogVisible: false,
      isDuplicateDialogVisible: false,
      isConfirmationCreateDialogVisible: false,
      isConfirmationEditDialogVisible: false,

      // ----------------------------------------------------------------------
      searchQuery: '',
      bindingsSearchQuery: '',

      selectedRow: null,
      productNameToCreate: null,

      selectedStatusId: null,

      // Actions
      actions: {
        // ----------------------------------------------------------------------
        // Modals
        showFilterPanel: () => set({ isFilterPanelVisible: true }, false, 'showFilterPanel'),
        hideFilterPanel: () => set({ isFilterPanelVisible: false }, false, 'hideFilterPanel'),
        openCreateModal: () => set({ isCreateModalVisible: true }, false, 'openCreateModal'),
        closeCreateModal: () => set({ isCreateModalVisible: false }, false, 'closeCreateModal'),
        openDuplicateModal: () => set({ isDuplicateModalVisible: true }, false, 'openDuplicateModal'),
        closeDuplicateModal: () => set({ isDuplicateModalVisible: false }, false, 'closeDuplicateModal'),
        openEditModal: () => set({ isEditModalVisible: true }, false, 'openEditModal'),
        closeEditModal: () => set({ isEditModalVisible: false }, false, 'closeEditModal'),

        // ----------------------------------------------------------------------
        // Dialogs
        openDeleteDialog: () => set({ isDeleteDialogVisible: true }, false, 'openDeleteDialog'),
        closeDeleteDialog: () => set({ isDeleteDialogVisible: false }, false, 'closeDeleteDialog'),
        openDuplicateDialog: () => set({ isDuplicateDialogVisible: true }, false, 'openDuplicateDialog'),
        closeDuplicateDialog: () => set({ isDuplicateDialogVisible: false }, false, 'closeDuplicateDialog'),
        openConfirmationCreateDialog: () =>
          set({ isConfirmationCreateDialogVisible: true }, false, 'openConfirmationCreateDialog'),
        closeConfirmationCreateDialog: () =>
          set({ isConfirmationCreateDialogVisible: false }, false, 'closeConfirmationCreateDialog'),
        openConfirmationEditDialog: () =>
          set({ isConfirmationEditDialogVisible: true }, false, 'openConfirmationEditDialog'),
        closeConfirmationEditDialog: () =>
          set({ isConfirmationEditDialogVisible: false }, false, 'closeConfirmationEditDialog'),

        // ----------------------------------------------------------------------

        setSearchQuery: searchQuery => set({ searchQuery, selectedRow: null }, false, 'setSearchQuery'),
        clearSearchQuery: () => set({ searchQuery: '', selectedRow: null }, false, 'clearSearchQuery'),

        setBindingsSearchQuery: bindingsSearchQuery =>
          set({ bindingsSearchQuery, selectedRow: null }, false, 'setBindingsSearchQuery'),
        clearBindingsSearchQuery: () =>
          set({ bindingsSearchQuery: '', selectedRow: null }, false, 'clearBindingsSearchQuery'),

        setPreferredTab: preferredTab => set({ preferredTab }, false, 'setPreferredTab'),
        setActiveTab: activeTab => set({ activeTab }, false, 'setActiveTab'),

        setSelectedRow: selectedRow => set({ selectedRow }, false, 'setSelectedRow'),
        resetSelectedRow: () => set({ selectedRow: null }, false, 'resetSelectedRow'),

        setProductNameToCreate: productNameToCreate => set({ productNameToCreate }, false, 'setProductNameToCreate'),
        resetProductNameToCreate: () => set({ productNameToCreate: null }, false, 'resetProductNameToCreate'),

        setVariant: variant => set({ variant }, false, 'setVariant'),

        setSelectedStatusId: statusId =>
          set({
            selectedStatusId: statusId,
          }),
        resetSelectedStatusId: () =>
          set({
            selectedStatusId: null,
          }),
      },
    })),
    { name: 'useNomenclatureStore' },
  ),
);

// ----------------------------------------------------------------------
// State selectors

export const selectNomenclatureStoreIsFilterPanelVisible = (state: NomenclatureStore) => state.isFilterPanelVisible;
export const selectNomenclatureStoreSelectedRow = (state: NomenclatureStore) => state.selectedRow;
export const selectNomenclatureStoreProductNameToCreate = (state: NomenclatureStore) => state.productNameToCreate;

export const selectNomenclatureStoreContextWindowsState = (state: NomenclatureStore) => ({
  isCreateModalVisible: state.isCreateModalVisible,
  isDuplicateModalVisible: state.isDuplicateModalVisible,
  isEditModalVisible: state.isEditModalVisible,

  isDuplicateDialogVisible: state.isDuplicateDialogVisible,
  isDeleteDialogVisible: state.isDeleteDialogVisible,
  isConfirmationCreateDialogVisible: state.isConfirmationCreateDialogVisible,
  isConfirmationEditDialogVisible: state.isConfirmationEditDialogVisible,
});
export const selectNomenclatureStoreIsContextWindowsVisible = (state: NomenclatureStore) =>
  some(selectNomenclatureStoreContextWindowsState(state));

export const selectNomenclatureStoreSearchQuery = (state: NomenclatureStore) => state.searchQuery;
export const selectBindingsNomenclatureStoreSearchQuery = (state: NomenclatureStore) => state.bindingsSearchQuery;

export const selectActiveTab = (state: NomenclatureStore) => state.activeTab;
export const selectPreferredTab = (state: NomenclatureStore) => state.preferredTab;

export const selectVariant = (state: NomenclatureStore) => state.variant;

// ----------------------------------------------------------------------
// Actions selectors

export const selectNomenclatureStoreActions = (state: NomenclatureStore) => state.actions;

// ----------------------------------------------------------------------
// Hooks

export const useNomenclatureContextWindows = () =>
  useNomenclatureStore(selectNomenclatureStoreContextWindowsState, shallow);
export const useNomenclatureIsContextWindowsVisible = () => {
  const contextWindowsIsVisible = useNomenclatureStore(selectNomenclatureStoreContextWindowsState, shallow);

  return some(contextWindowsIsVisible);
};
export const useNomenclatureStorePreferredTab = () => useNomenclatureStore(selectPreferredTab, shallow);
export const useNomenclatureStoreActiveTab = () => useNomenclatureStore(selectActiveTab, shallow);

export const useResetNomenclatureStoreOnUnmount = () => useUnmount(resetNomenclatureStore);
export const useNomenclatureStoreActions = () => useNomenclatureStore(selectNomenclatureStoreActions, shallow);
export const useNomenclatureStoreContextWindowsState = () =>
  useNomenclatureStore(selectNomenclatureStoreContextWindowsState, shallow);
export const useNomenclatureStoreIsContextWindowsVisible = () =>
  useNomenclatureStore(selectNomenclatureStoreIsContextWindowsVisible);

export const useBindingsNomenclatureStoreSearchQuery = () => useNomenclatureStore(store => store.bindingsSearchQuery);
export const useNomenclatureStoreSelectedStatusId = () => useNomenclatureStore(store => store.selectedStatusId);
